/**
 * Header Component
 * 
 * This component provides the main navigation header for the application.
 * It includes a responsive design with a hamburger menu for mobile devices,
 * smooth scrolling navigation, and active section highlighting.
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const HeaderContainer = styled.header`
  /* Enhanced glassmorphism for header - more transparent */
  background: rgba(40, 40, 40, 0.25);
  padding: var(--spacing-md);
  position: sticky;
  top: 0;
  z-index: 1000;
  width: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  /* Premium easing from Lando Norris site */
  transition: all var(--duration-medium) var(--ease-premium);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid rgba(235, 219, 178, 0.1);
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Roboto', sans-serif;
  height: 56px;
`;

/**
 * Styled monogram logo with 16-bit pixel font styling
 * Uses Press Start 2P for authentic retro gaming aesthetic
 */
const Logo = styled.a`
  font-size: 1.2rem;
  font-weight: 400;
  color: #ebdbb2; /* Gruvbox foreground color */
  font-family: 'Press Start 2P', cursive;
  text-decoration: none;
  cursor: pointer;
  /* Premium easing from Lando Norris site */
  transition: all var(--duration-fast) var(--ease-premium);
  display: inline-block;
  padding: 4px 8px;
  /* Subtle text depth for logo */
  text-shadow: 0px 1px rgba(184, 187, 38, 0.15);

  &:hover {
    color: #b8bb26; /* Gruvbox green - primary color */
    transform: scale(1.1);
    text-shadow: 0px 2px rgba(184, 187, 38, 0.25);
  }
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 999;
    opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition: opacity 0.3s ease, visibility 0.3s ease;
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
  }
`;

const MenuItems = styled.ul`
  display: flex;
  list-style: none;
  margin: 0;
  padding: 0;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    position: fixed;
    top: 0;
    right: 0;
    width: 300px;
    height: 100vh;
    /* Glassmorphism for mobile menu with improved legibility */
    background: rgba(29, 32, 33, 0.92);
    padding: 80px 0 20px;
    z-index: 1000;
    backdrop-filter: blur(20px) saturate(180%);
    -webkit-backdrop-filter: blur(20px) saturate(180%);
    border-left: 1px solid rgba(235, 219, 178, 0.2);
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-y: auto;
    box-shadow: ${({ $isOpen }) => ($isOpen ? '-4px 0 30px rgba(0, 0, 0, 0.3)' : 'none')};
  }
`;

const MenuItem = styled.li`
  margin-left: var(--spacing-xl);

  @media (max-width: 768px) {
    margin: 0;
    padding: var(--spacing-md) var(--spacing-lg);
    text-align: left;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '20px')});
    opacity: ${({ $isOpen }) => ($isOpen ? '1' : '0')};
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease;
    transition-delay: ${({ $index }) => `${$index * 0.05}s`};

    &:last-child {
      border-bottom: none;
    }
  }
`;

const MenuLink = styled.a`
  color: var(--text-medium);
  text-decoration: none;
  font-weight: 500;
  font-size: 0.875rem;
  letter-spacing: 0.25px;
  /* Premium easing from Lando Norris site */
  transition: all var(--duration-fast) var(--ease-premium);
  position: relative;
  padding: var(--spacing-sm) 0;
  display: block;

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: var(--spacing-md) 0;
    
    &:active {
      transform: scale(0.98);
    }
  }
  
  @media (max-width: 768px) {
    padding: var(--spacing-md) 0;
    font-size: 1.25rem;
  }
  
  &:hover, &.active {
    color: var(--primary);
  }
  
  @media (min-width: 769px) {
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      width: 0;
      height: 2px;
      background-color: var(--primary);
      transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    &:hover::after, &.active::after {
      width: 100%;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: var(--text);
  font-size: 1.5rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 1001;
  padding: 0;
  outline: none;
  
  &:hover {
    color: var(--primary);
    background-color: rgba(255, 255, 255, 0.08);
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.12);
  }
  
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--primary);
  }
  
  @media (max-width: 768px) {
    display: flex;
  }
`;

const HamburgerIcon = styled.div`
  width: 24px;
  height: 24px;
  position: relative;
  margin: 0 auto;
  transform: rotate(0deg);
  transition: 0.3s ease;
  cursor: pointer;

  span {
    display: block;
    position: absolute;
    height: 2px;
    width: 100%;
    background: var(--text);
    border-radius: 1px;
    opacity: 1;
    left: 0;
    transform: rotate(0deg);
    transition: 0.25s ease;
    transform-origin: center;
  }

  span:nth-child(1) {
    top: ${({ $isOpen }) => ($isOpen ? '11px' : '6px')};
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(45deg)' : 'none')};
  }

  span:nth-child(2) {
    top: 11px;
    opacity: ${({ $isOpen }) => ($isOpen ? '0' : '1')};
  }

  span:nth-child(3) {
    top: ${({ $isOpen }) => ($isOpen ? '11px' : '16px')};
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(-45deg)' : 'none')};
    height: 2px;
    background-color: var(--text);
    transition: all 0.3s ease;
    left: 0;
  }

  &::before {
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(45deg)' : 'none')};
    top: ${({ $isOpen }) => ($isOpen ? '0' : '-6px')};
  }

  &::after {
    transform: ${({ $isOpen }) => ($isOpen ? 'rotate(-45deg)' : 'none')};
    top: ${({ $isOpen }) => ($isOpen ? '0' : '6px')};
  }
`;

/**
 * Header component that provides the main navigation for the website
 * Features responsive design, mobile menu, and scroll position tracking
 * 
 * @returns {JSX.Element} The header component with navigation
 */
const Header = () => {
  // State for mobile menu toggle
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // State to track which section is currently active based on scroll position
  const [activeSection, setActiveSection] = useState('about');
  
  // Handle body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  
  /**
   * Handles smooth scrolling to sections and closes the mobile menu
   * @param {Event} e - The click event
   * @param {string} sectionId - The ID of the section to scroll to
   */
  const handleNavClick = (e, sectionId) => {
    e.preventDefault();
    setIsMenuOpen(false);
    
    const section = document.getElementById(sectionId);
    if (section) {
      // Calculate the scroll position with offset for header
      const headerHeight = 80; // Same as scroll-margin-top in CSS
      const sectionPosition = section.getBoundingClientRect().top;
      const offsetPosition = sectionPosition + window.pageYOffset - headerHeight;
      
      // Remove highlight class from all sections first
      document.querySelectorAll('section').forEach(s => {
        s.classList.remove('section-highlight');
      });
      
      // Smooth scroll with animation
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      
      // Add highlight animation class after a small delay to ensure scrolling has started
      setTimeout(() => {
        section.classList.add('section-highlight');
        // Remove the class after animation completes to allow it to be triggered again
        setTimeout(() => {
          section.classList.remove('section-highlight');
        }, 1000); // Match the animation duration
      }, 100);
    }
  };
  
  /**
   * Effect hook to handle scroll events and update active section
   * This determines which navigation item should be highlighted based on
   * the current scroll position of the page
   */
  useEffect(() => {
    /**
     * Scroll event handler that determines which section is currently in view
     * Updates the activeSection state accordingly
     */
    const handleScroll = () => {
      const sections = ['about', 'certifications', 'ai-collaboration', 'contact'];
      
      // Check each section's position and update active state
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          // Consider a section active when its top part is near the viewport top
          if (rect.top <= 100 && rect.bottom >= 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };
    
    // Add scroll listener when component mounts
    window.addEventListener('scroll', handleScroll);
    
    // Clean up the listener when component unmounts
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <HeaderContainer>
      <Nav>
        <Logo href="#" onClick={(e) => {
          e.preventDefault();
          // Add a more sophisticated animation for scrolling to top
          window.scrollTo({ 
            top: 0, 
            behavior: 'smooth' 
          });
        }}>JT</Logo>
        <>
        <Overlay $isOpen={isMenuOpen} onClick={() => setIsMenuOpen(false)} />
        <MenuButton
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
        >
          <HamburgerIcon $isOpen={isMenuOpen}>
              <span></span>
              <span></span>
              <span></span>
          </HamburgerIcon>
        </MenuButton>
        <MenuItems $isOpen={isMenuOpen}>
          <MenuItem $isOpen={isMenuOpen} $index={0}>
            <MenuLink
              href="#about"
              className={activeSection === 'about' ? 'active' : ''}
              onClick={(e) => handleNavClick(e, 'about')}
            >
              About
            </MenuLink>
          </MenuItem>
          <MenuItem $isOpen={isMenuOpen} $index={1}>
            <MenuLink
              href="#certifications"
              className={activeSection === 'certifications' ? 'active' : ''}
              onClick={(e) => handleNavClick(e, 'certifications')}
            >
              Certifications
            </MenuLink>
          </MenuItem>
          <MenuItem $isOpen={isMenuOpen} $index={2}>
            <MenuLink
              href="#ai-collaboration"
              className={activeSection === 'ai-collaboration' ? 'active' : ''}
              onClick={(e) => handleNavClick(e, 'ai-collaboration')}
            >
              AI Collaboration
            </MenuLink>
          </MenuItem>
          <MenuItem $isOpen={isMenuOpen} $index={3}>
            <MenuLink
              href="#contact"
              className={activeSection === 'contact' ? 'active' : ''}
              onClick={(e) => handleNavClick(e, 'contact')}
            >
              Contact
            </MenuLink>
          </MenuItem>
        </MenuItems>
        </>
      </Nav>
    </HeaderContainer>
  );
};

/**
 * Export the Header component wrapped in React.memo to prevent unnecessary re-renders
 * This is important for performance as the header is always visible and contains
 * interactive elements that shouldn't re-render unless their props change
 */
export default React.memo(Header);
